import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Plus, BarChart3 } from "lucide-react";
import { CampaignStatusPill, deriveStatus, TRANSIENT_STATUSES } from "./dashboard/CampaignStatusPill";

// Polling cadence for transient states (payment_pending / activating).
// Chosen empirically: 4s feels alive on the dashboard without hammering
// Mongo — Stripe webhook + PS activation typically flip a campaign in
// under 30s so a client seldom sees more than 8 polls before the pill
// settles into "Live · Fielding".
const POLL_MS = 4000;

export default function ClientDashboard() {
  const { profile } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const pollTimer = useRef(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const { data } = await api.get("/campaigns");
        if (!mounted) return;
        setCampaigns(data);
        // Only poll while at least one campaign is in a transient state
        // (payment_pending or activating). Once every card settles into
        // draft/live/filled/paused/stopped the interval clears — cheap
        // to run for clients with dozens of static campaigns.
        const anyTransient = (data || []).some(
          (c) => TRANSIENT_STATUSES.has(deriveStatus(c)),
        );
        if (pollTimer.current) {
          clearTimeout(pollTimer.current);
          pollTimer.current = null;
        }
        if (anyTransient && mounted) {
          pollTimer.current = setTimeout(load, POLL_MS);
        }
      } catch (err) {
        // Silent — the UI already renders whatever state it last saw.
        // Retry on the same cadence to survive transient auth blips.
        if (mounted) {
          pollTimer.current = setTimeout(load, POLL_MS * 2);
        }
      }
    }
    load();
    return () => {
      mounted = false;
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[var(--rl-bg-subtle)]" data-testid="client-dashboard">
      <Navigation />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)]">Client Dashboard</div>
            <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-2">{profile?.company_name || "Workspace"}</h1>
            <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">Launch voice-first qualitative studies in minutes. Get a boardroom deck the moment data hits 100%.</p>
          </div>
          <Link
            to="/client/campaigns/new"
            className="inline-flex items-center gap-2 bg-[var(--rl-text)] text-white px-5 py-3 font-semibold hover:bg-[var(--rl-brand)] transition-colors"
            data-testid="new-campaign-btn"
          >
            <Plus className="w-4 h-4" /> New Campaign
          </Link>
        </div>

        <div className="mt-12">
          <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Your campaigns</h2>
          {campaigns.length === 0 ? (
            <div className="bg-white border border-dashed border-[var(--rl-border)] p-12 text-center" data-testid="campaigns-empty-state">
              <BarChart3 className="w-10 h-10 mx-auto text-[var(--rl-text-secondary)]" />
              <h3 className="font-display font-bold text-xl mt-4">No campaigns yet</h3>
              <p className="text-sm text-[var(--rl-text-secondary)] mt-2">Build your first study, choose your audience mix, and let the AI conduct the interviews.</p>
              <Link to="/client/campaigns/new" className="inline-block mt-6 text-[var(--rl-brand)] font-semibold underline" data-testid="empty-state-new-campaign-link">
                Create your first campaign →
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="campaigns-grid">
              {campaigns.map((c) => {
                const pct = c.target_completes > 0 ? Math.round((c.current_completes / c.target_completes) * 100) : 0;
                return (
                  <Link
                    key={c.id}
                    to={`/client/campaigns/${c.id}`}
                    className="block bg-white border border-[var(--rl-border)] hover:border-[var(--rl-brand)] p-6 transition-colors"
                    data-testid={`campaign-card-${c.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <CampaignStatusPill campaign={c} />
                      <span className="font-mono text-xs text-[var(--rl-text-secondary)]">${c.total_budget.toFixed(2)}</span>
                    </div>
                    <h3 className="font-display font-bold text-lg mt-4 leading-tight">{c.title}</h3>
                    <div className="mt-5">
                      <div className="flex items-center justify-between text-xs text-[var(--rl-text-secondary)] mb-1">
                        <span className="font-mono">{c.current_completes} / {c.target_completes}</span>
                        <span className="font-mono">{pct}%</span>
                      </div>
                      <div className="h-1.5 bg-[var(--rl-bg-subtle)]">
                        <div className="h-full bg-[var(--rl-brand)]" style={{ width: `${Math.min(pct, 100)}%`, transition: "width 400ms ease" }} />
                      </div>
                    </div>
                    {c.status === "pending_payment" && (
                      <div
                        className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-[var(--rl-text)] border-b border-[var(--rl-text)] hover:text-[var(--rl-brand)] hover:border-[var(--rl-brand)] transition-colors"
                        data-testid={`campaign-card-pay-cta-${c.id}`}
                      >
                        Complete payment →
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
